import React, { Fragment, useState, useEffect, useRef } from "react";
import { Link, useLocation as useRouteLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";

import Logo from "../../images/output-onlinepngtools-removebg-preview.png";

import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import DashboardIcon from "@material-ui/icons/Dashboard";

import { useLocation } from "../../context/LocationContext";
import LocationModal from "../marketplace/LocationModal";
import "../../css/navbar.css";

import { allcartItemAction } from "../../redux/actions/cartAction";
import { allProductToWishListAction } from "../../redux/actions/wishListAction";
import { userLogoutAction } from "../../redux/actions/userAction";
import {
  clearErrorAction,
  getAllNotificationaActionByUser,
  readNotificationaActionByUser,
} from "../../redux/actions/notificationAction";

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
};

function Navbar() {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeLocation = useRouteLocation();
  const { location, setIsLocationModalOpen } = useLocation();

  const [mobileMenu, setMobileMenu] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const notificationRef = useRef(null);
  const accountRef = useRef(null);

  const { isAuthenticated, user } = useSelector((state) => state.user);
  const { carts } = useSelector((state) => state.carts);
  const { wishListProducts } = useSelector((state) => state.wishListAll);
  const hasWishlistLoaded = Array.isArray(wishListProducts);

  const { notification, error } = useSelector(
    (state) => state.userNotification
  );

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileMenu(false);
    setNotificationOpen(false);
    setAccountMenuOpen(false);
  }, [routeLocation.pathname]);

  // Click outside listener for dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(allcartItemAction());
      if (!hasWishlistLoaded) {
        dispatch(allProductToWishListAction());
      }
      dispatch(getAllNotificationaActionByUser());
    }
  }, [dispatch, hasWishlistLoaded, isAuthenticated]);

  useEffect(() => {
    if (error) {
      swal({
        title: "Error",
        text: error,
        icon: "error",
      });
      dispatch(clearErrorAction());
    }
  }, [dispatch, error]);

  const unreadNotifications =
    notification &&
    notification.filter((item) => item.notifactionRead === false);

  const handleReadNotification = (id) => {
    dispatch(readNotificationaActionByUser(id));
    setNotificationOpen(false);
  };

  const handleLogout = () => {
    dispatch(userLogoutAction());
    setAccountMenuOpen(false);
    setMobileMenu(false);
    swal({
      title: "Logged Out",
      text: "You have been logged out successfully.",
      icon: "success",
      timer: 1500,
      buttons: false,
    });
    history.push("/Login");
  };

  const isNearbyShopsActive =
    routeLocation.pathname.startsWith("/marketplace") &&
    routeLocation.search.includes("type=shops");

  const isCatalogueActive = routeLocation.pathname === "/Product";

  const isHomeActive = routeLocation.pathname === "/";

  return (
    <Fragment>
      {/* Spacer to prevent fixed navbar from covering top page content */}
      <div className="navbar-spacer" />

      {/* Modern Redesigned Top Header */}
      <nav className="modern-navbar" aria-label="Main Navigation">
        {/* LEFT: LOGO & LOCATION SELECTOR */}
        <div className="nav-left">
          <Link to="/" className="navbar-logo" aria-label="IM Online Home">
            <img src={Logo} alt="IM Online Shopping" />
          </Link>

          <button
            type="button"
            className="nav-location-btn"
            onClick={() => setIsLocationModalOpen(true)}
            title="Click to change your delivery location"
          >
            <LocationOnIcon className="nav-loc-icon" />
            <span className="nav-loc-text">{location.label || "Select Location"}</span>
            <KeyboardArrowDownIcon className="nav-loc-arrow" />
          </button>
        </div>

        {/* CENTER: DESKTOP NAVIGATION LINKS */}
        <ul className="nav-center">
          <li>
            <Link to="/" className={`nav-link ${isHomeActive ? "active" : ""}`}>
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/marketplace/search?type=shops"
              className={`nav-link ${isNearbyShopsActive ? "active" : ""}`}
            >
              <StorefrontIcon className="nav-link-icon" />
              <span>Nearby Shops</span>
            </Link>
          </li>
          <li>
            <Link
              to="/Product"
              className={`nav-link ${isCatalogueActive ? "active" : ""}`}
            >
              Catalogue
            </Link>
          </li>
        </ul>

        {/* RIGHT: ACTIONS (SEARCH, WISHLIST, ACCOUNT, NOTIFICATIONS, CART) */}
        <div className="nav-right">
          {/* Search Trigger */}
          <Link
            to="/marketplace/search"
            aria-label="Search marketplace"
            className="nav-icon-btn nav-search-btn"
            title="Search products and shops"
          >
            <SearchIcon />
          </Link>

          {/* Wishlist Button */}
          <Link
            to="/user/wishlist"
            aria-label="Wishlist"
            className="nav-icon-btn nav-wishlist-btn"
            title="Saved wishlist items"
          >
            <FavoriteBorderOutlinedIcon />
            {wishListProducts && wishListProducts.length > 0 && (
              <span className="nav-badge wishlist-badge">
                {wishListProducts.length}
              </span>
            )}
          </Link>

          {/* Notifications Dropdown */}
          {isAuthenticated && (
            <div className="nav-dropdown-wrapper" ref={notificationRef}>
              <button
                type="button"
                className={`nav-icon-btn ${notificationOpen ? "active" : ""}`}
                aria-label="Notifications"
                onClick={() => setNotificationOpen(!notificationOpen)}
                title="Notifications"
              >
                <NotificationsNoneOutlinedIcon />
                {unreadNotifications && unreadNotifications.length > 0 && (
                  <span className="nav-badge notification-badge">
                    {unreadNotifications.length}
                  </span>
                )}
              </button>

              {notificationOpen && (
                <div className="nav-dropdown-menu notification-menu">
                  <div className="nav-dropdown-header">
                    <strong>Notifications</strong>
                    {unreadNotifications?.length > 0 && (
                      <span className="nav-unread-chip">
                        {unreadNotifications.length} new
                      </span>
                    )}
                  </div>
                  <div className="nav-notification-list">
                    {notification && notification.length > 0 ? (
                      notification.slice(0, 8).map((item) => (
                        <div
                          key={item._id}
                          className={`nav-notification-item ${
                            item.notifactionRead ? "read" : "unread"
                          }`}
                          onClick={() => handleReadNotification(item._id)}
                        >
                          <div className="nav-notif-dot" />
                          <span>{safeText(item.message, "Notification update")}</span>
                        </div>
                      ))
                    ) : (
                      <div className="nav-dropdown-empty">
                        No notifications right now.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Dropdown or Sign In */}
          {isAuthenticated ? (
            <div className="nav-dropdown-wrapper" ref={accountRef}>
              <button
                type="button"
                className={`nav-account-btn ${accountMenuOpen ? "active" : ""}`}
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                aria-label="Account options"
              >
                <div className="nav-account-avatar">
                  {user?.firstName?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <span className="nav-account-name">
                  {user?.firstName || "Account"}
                </span>
                <KeyboardArrowDownIcon className="nav-account-arrow" />
              </button>

              {accountMenuOpen && (
                <div className="nav-dropdown-menu account-menu">
                  <div className="nav-dropdown-profile-header">
                    <strong>{user?.firstName} {user?.lastName}</strong>
                    <small>{user?.email}</small>
                  </div>

                  <div className="nav-dropdown-links">
                    <Link
                      to="/userprofile"
                      className="nav-dropdown-item"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      <PersonOutlineIcon />
                      <span>My Profile</span>
                    </Link>
                    <Link
                      to="/orders/Details/me"
                      className="nav-dropdown-item"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      <ListAltIcon />
                      <span>My Orders</span>
                    </Link>
                    <Link
                      to="/user/wishlist"
                      className="nav-dropdown-item"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      <FavoriteBorderOutlinedIcon />
                      <span>Saved Wishlist</span>
                    </Link>
                    {user?.role === "admin" && (
                      <Link
                        to="/Admin/dashboard"
                        className="nav-dropdown-item admin-link"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <DashboardIcon />
                        <span>Admin Dashboard</span>
                      </Link>
                    )}
                  </div>

                  <div className="nav-dropdown-footer">
                    <button
                      type="button"
                      className="nav-logout-btn"
                      onClick={handleLogout}
                    >
                      <ExitToAppIcon />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/Login" className="nav-signin-btn">
              <PersonOutlineIcon className="signin-icon" />
              <span>Sign In</span>
            </Link>
          )}

          {/* Shopping Bag / Cart Button */}
          <Link to="/cart" className="nav-cart-btn" aria-label="Shopping bag">
            <ShoppingBagOutlinedIcon />
            {carts && carts.length > 0 && (
              <span className="nav-badge cart-badge">{carts.length}</span>
            )}
          </Link>

          {/* Hamburger Menu Trigger for Mobile */}
          <button
            type="button"
            className="nav-hamburger-btn"
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label={mobileMenu ? "Close menu" : "Open menu"}
          >
            {mobileMenu ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </nav>

      {/* MOBILE DRAWER OVERLAY */}
      {mobileMenu && (
        <div
          className="nav-drawer-backdrop"
          onClick={() => setMobileMenu(false)}
        />
      )}

      {/* MOBILE SLIDE-IN DRAWER */}
      <aside
        className={`nav-mobile-drawer ${mobileMenu ? "open" : ""}`}
        aria-label="Mobile Drawer Navigation"
      >
        <div className="nav-drawer-top">
          {isAuthenticated ? (
            <div className="nav-drawer-user-info">
              <div className="nav-drawer-avatar">
                {user?.firstName?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                <strong>{user?.firstName} {user?.lastName}</strong>
                <small>{user?.email}</small>
              </div>
            </div>
          ) : (
            <div className="nav-drawer-guest-info">
              <strong>Welcome to IM Online</strong>
              <small>Discover local shops & fast delivery</small>
            </div>
          )}
          <button
            type="button"
            className="nav-drawer-close-btn"
            onClick={() => setMobileMenu(false)}
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        {!isAuthenticated && (
          <div className="nav-drawer-auth-actions">
            <Link
              to="/Login"
              className="nav-drawer-btn primary"
              onClick={() => setMobileMenu(false)}
            >
              Sign In
            </Link>
            <Link
              to="/Signin"
              className="nav-drawer-btn secondary"
              onClick={() => setMobileMenu(false)}
            >
              Create Account
            </Link>
          </div>
        )}

        <div className="nav-drawer-nav-group">
          <span className="nav-drawer-section-title">Explore & Shop</span>
          <Link
            to="/"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <HomeOutlinedIcon />
            <span>Home</span>
          </Link>
          <Link
            to="/marketplace/search?type=shops"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <StorefrontIcon />
            <span>Nearby Local Shops</span>
          </Link>
          <Link
            to="/Product"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <WidgetsOutlinedIcon />
            <span>Catalogue & Products</span>
          </Link>
          <Link
            to="/marketplace/search"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <SearchIcon />
            <span>Search Marketplace</span>
          </Link>
        </div>

        <div className="nav-drawer-nav-group">
          <span className="nav-drawer-section-title">My Account</span>
          <Link
            to={isAuthenticated ? "/userprofile" : "/Login"}
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <PersonOutlineIcon />
            <span>Profile & Account</span>
          </Link>
          <Link
            to="/orders/Details/me"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <ListAltIcon />
            <span>My Orders</span>
          </Link>
          <Link
            to="/user/wishlist"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <FavoriteBorderOutlinedIcon />
            <span>Saved Wishlist</span>
            {wishListProducts && wishListProducts.length > 0 && (
              <span className="nav-drawer-badge">{wishListProducts.length}</span>
            )}
          </Link>
          <Link
            to="/cart"
            className="nav-drawer-link"
            onClick={() => setMobileMenu(false)}
          >
            <ShoppingBagOutlinedIcon />
            <span>Shopping Bag</span>
            {carts && carts.length > 0 && (
              <span className="nav-drawer-badge">{carts.length}</span>
            )}
          </Link>
          {user?.role === "admin" && (
            <Link
              to="/Admin/dashboard"
              className="nav-drawer-link admin"
              onClick={() => setMobileMenu(false)}
            >
              <DashboardIcon />
              <span>Admin Dashboard</span>
            </Link>
          )}
        </div>

        {isAuthenticated && (
          <div className="nav-drawer-bottom">
            <button
              type="button"
              className="nav-drawer-logout-btn"
              onClick={handleLogout}
            >
              <ExitToAppIcon />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </aside>

      {/* NATIVE MOBILE BOTTOM NAVIGATION BAR */}
      <nav className="mobile-bottom-nav" aria-label="Mobile Bottom Navigation">
        <Link
          to="/"
          className={`mob-nav-item ${isHomeActive ? "active" : ""}`}
        >
          <HomeOutlinedIcon className="mob-nav-icon" />
          <span className="mob-nav-label">Home</span>
        </Link>

        <Link
          to="/marketplace/search?type=shops"
          className={`mob-nav-item ${isNearbyShopsActive ? "active" : ""}`}
        >
          <StorefrontOutlinedIcon className="mob-nav-icon" />
          <span className="mob-nav-label">Shops</span>
        </Link>

        <Link
          to="/marketplace/search"
          className={`mob-nav-item ${
            (routeLocation.pathname.startsWith("/marketplace") &&
              !routeLocation.search.includes("type=shops")) ||
            routeLocation.pathname === "/Product"
              ? "active"
              : ""
          }`}
        >
          <WidgetsOutlinedIcon className="mob-nav-icon" />
          <span className="mob-nav-label">Explore</span>
        </Link>

        <Link
          to="/user/wishlist"
          className={`mob-nav-item ${
            routeLocation.pathname.includes("/wishlist") ? "active" : ""
          }`}
        >
          <div className="mob-nav-icon-badge">
            <FavoriteBorderOutlinedIcon className="mob-nav-icon" />
            {wishListProducts && wishListProducts.length > 0 && (
              <span className="mob-nav-badge">{wishListProducts.length}</span>
            )}
          </div>
          <span className="mob-nav-label">Wishlist</span>
        </Link>

        <Link
          to={isAuthenticated ? "/userprofile" : "/Login"}
          className={`mob-nav-item ${
            routeLocation.pathname.includes("/userprofile") ||
            routeLocation.pathname.includes("/Login") ||
            routeLocation.pathname.includes("/order")
              ? "active"
              : ""
          }`}
        >
          <PersonOutlineIcon className="mob-nav-icon" />
          <span className="mob-nav-label">{isAuthenticated ? "Account" : "Login"}</span>
        </Link>
      </nav>

      {/* Global Location Selection Modal */}
      <LocationModal />
    </Fragment>
  );
}

export default Navbar;
